'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { authApi } from '@/features/auth/api';
import type { Usuario, Empresa } from '@/features/auth/types';

interface AuthState {
  // Estado
  token: string | null;
  refreshToken: string | null;
  usuario: Usuario | null;
  empresa: Empresa | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Acciones
  setAuth: (data: { token: string; refreshToken: string; usuario: Usuario; empresa: Empresa }) => void;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<boolean>;
  checkAuth: () => Promise<boolean>;
  hidratarTokenDesdeCookie: () => Promise<boolean>;
}

// Nombre de la cookie de autenticación (debe coincidir con el que se escribe en verificar/page.tsx y el que lee el middleware)
const COOKIE_NOMBRE_TOKEN = 'jf-auth-token';

// Helper: lee el token desde document.cookie (solo en cliente)
function leerTokenDesdeCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    const idx = cookie.indexOf('=');
    if (idx === -1) continue;
    const nombre = cookie.substring(0, idx);
    if (nombre === COOKIE_NOMBRE_TOKEN) {
      return decodeURIComponent(cookie.substring(idx + 1));
    }
  }
  return null;
}

// Helper: elimina la cookie de autenticación
function borrarCookieToken(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NOMBRE_TOKEN}=; path=/; max-age=0`;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Estado inicial
      token: null,
      refreshToken: null,
      usuario: null,
      empresa: null,
      isLoading: true,
      isAuthenticated: false,

      // Establecer autenticación después de login
      setAuth: (data) => {
        set({
          token: data.token,
          refreshToken: data.refreshToken,
          usuario: data.usuario,
          empresa: data.empresa,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      // Cerrar sesión
      logout: async () => {
        const { token } = get();

        // Intentar cerrar sesión en el servidor
        if (token) {
          try {
            await authApi.logout(token);
          } catch (error) {
            console.warn('[auth] No se pudo revocar el token en el servidor. Expirará naturalmente.', error instanceof Error ? error.message : error);
          }
        }

        // Borrar cookie
        borrarCookieToken();

        // Limpiar estado local
        set({
          token: null,
          refreshToken: null,
          usuario: null,
          empresa: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      // Renovar token
      refreshAuth: async () => {
        const { refreshToken } = get();

        if (!refreshToken) {
          set({ isLoading: false, isAuthenticated: false });
          return false;
        }

        try {
          const response = await authApi.refreshToken(refreshToken);

          if (response.success && response.data) {
            set({
              token: response.data.token,
              refreshToken: response.data.refreshToken,
              isLoading: false,
            });
            return true;
          }

          // Token inválido, cerrar sesión
          await get().logout();
          return false;
        } catch (error) {
          console.error('Error al renovar token:', error);
          await get().logout();
          return false;
        }
      },

      // Verificar autenticación actual
      checkAuth: async () => {
        const { token } = get();

        if (!token) {
          set({ isLoading: false, isAuthenticated: false });
          return false;
        }

        try {
          const response = await authApi.me(token);

          if (response.success && response.data) {
            set({
              usuario: response.data.usuario,
              empresa: response.data.empresa,
              isAuthenticated: true,
              isLoading: false,
            });
            return true;
          }

          // Token expirado, intentar renovar
          return await get().refreshAuth();
        } catch (error) {
          console.error('Error al verificar auth:', error);
          // Intentar renovar si el token expiró
          return await get().refreshAuth();
        }
      },

      // Hidratar token desde cookie tras F5 (el token ya no se persiste en localStorage)
      // Lee la cookie, setea el token en memoria y dispara checkAuth para validar contra el backend
      hidratarTokenDesdeCookie: async () => {
        const tokenCookie = leerTokenDesdeCookie();

        if (!tokenCookie) {
          set({ isLoading: false, isAuthenticated: false });
          return false;
        }

        // Setear el token en memoria (no tenemos refreshToken desde cookie, queda null)
        set({ token: tokenCookie, isLoading: true });

        // Validar contra backend
        return await get().checkAuth();
      },
    }),
    {
      name: 'johnfox-auth',
      storage: createJSONStorage(() => localStorage),
      // IMPORTANTE: NO persistir token/refreshToken en localStorage (riesgo XSS).
      // El token vive en cookie + memoria. En F5 se rehidrata desde cookie via hidratarTokenDesdeCookie().
      partialize: (state) => ({
        usuario: state.usuario,
        empresa: state.empresa,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Después de rehidratar (sólo usuario/empresa/isAuthenticated), recuperar el token desde la cookie.
        //
        // CRITICO: diferir la llamada async con queueMicrotask. zustand persist
        // invoca esta callback SINCRONO durante la inicializacion del modulo
        // (`create(persist(...))`). hidratarTokenDesdeCookie() llama a checkAuth()
        // → authApi.me() → apiRequest() → useAuthStore.getState() (self-reference).
        // En bundles minificados (Turbopack/Next 16) eso dispara
        // "Cannot access 'X' before initialization" porque la export de
        // useAuthStore aun no terminó de evaluarse cuando se la accede.
        // queueMicrotask defiere a la siguiente microtarea — para entonces el
        // modulo ya esta completamente inicializado.
        if (state) {
          state.isLoading = true;
          queueMicrotask(() => {
            state.hidratarTokenDesdeCookie();
          });
        }
      },
    }
  )
);

// Hook helper para obtener el token actual
export function useToken() {
  return useAuthStore((state) => state.token);
}

// Hook helper para verificar si está autenticado
export function useIsAuthenticated() {
  return useAuthStore((state) => state.isAuthenticated);
}

// Hook helper para obtener el usuario
export function useUser() {
  return useAuthStore((state) => state.usuario);
}

// Hook helper para obtener la empresa/negocio
export function useEmpresa() {
  return useAuthStore((state) => state.empresa);
}

// Hook helper para obtener el tenant
export function useTenant() {
  return useAuthStore((state) => state.usuario?.tenant);
}
